<script module lang="ts">
  const setPage = (selectedDate: string | undefined) => {
    const d = selectedDate ? parseDate(selectedDate) : new Date();
    return {
      year: d!.getUTCFullYear(),
      month: d!.getUTCMonth(),
    };
  };

  // TODO different for each mode?
  export const selectedDate = new PersistedState('archive-date-daily', '');
  export const calendarPage = $state<{ current: CalendarMonth }>({
    current: setPage(selectedDate.current),
  });
</script>

<script lang="ts">
  import ArrowLeft from '~icons/mdi/arrow-left';
  import ArrowRight from '~icons/mdi/arrow-right';
  import ActionButton from '~/components/ActionButton.svelte';
  import Star from '~/icons/Star.svelte';
  import type { CalendarMonth, DateCell } from '~/types';
  import { getNextMonth, getPrevMonth, parseDate } from '~/utils/dates';
  import { calendarQuery, getDailyMode } from '~/lib/queries.svelte';
  import StarContainer from './StarContainer.svelte';
  import { Tween } from 'svelte/motion';
  import { puzzleSummaryModal } from './PuzzleSummary.svelte';
  import Spinner from '~/components/Spinner.svelte';
  import { fade } from 'svelte/transition';
  import { PersistedState } from 'runed';
  import { page } from '$app/state';
  import type { GameMode } from '~/store/game.svelte';

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const dailyMode = $derived(getDailyMode(page.params.mode as GameMode));

  const calendar = $derived(calendarQuery(dailyMode, calendarPage.current));

  const monthStars = new Tween(0);
  const monthGreenStars = new Tween(0);
  $effect(() => {
    if (calendar.data) {
      const newStars =
        calendar.data.receivedStars - calendar.data.receivedGreenStars;
      const newGreenStars = calendar.data.receivedGreenStars;
      monthStars.set(newStars, { duration: newStars > 0 ? undefined : 0 });
      monthGreenStars.set(newGreenStars, {
        duration: newGreenStars > 0 ? undefined : 0,
      });
    }
  });

  const handlePrevMonth = () => {
    calendarPage.current = getPrevMonth(calendarPage.current);
  };

  const handleNextMonth = () => {
    calendarPage.current = getNextMonth(calendarPage.current);
  };

  const handleClickCell = (cell: DateCell) => {
    if (cell.disabled) return;
    puzzleSummaryModal.push(cell.date.split('T')[0]);
    selectedDate.current = cell.date;
  };

  let calendarBox = $state<DOMRect>();
  let headerHeight = $state<number>();
</script>

<div style:position="relative">
  {#if calendarBox && calendar.isFetching}
    <div
      class="loading"
      transition:fade
      style={`
        top: ${headerHeight}px;
        left: 0px;
        width: ${calendarBox.width}px;
        height: ${calendarBox.height}px;
      `}
    >
      <Spinner />
    </div>
  {/if}
  <table>
    <thead bind:clientHeight={headerHeight}>
      <tr>
        <th colspan="7">
          <div class="calendar-controls">
            <ActionButton iconOnly onClick={handlePrevMonth}>
              <ArrowLeft />
            </ActionButton>
            <div class="header">
              <span class="month">
                {new Date(
                  calendarPage.current.year,
                  calendarPage.current.month,
                  1,
                )?.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <span class="total-stars">
                {#if calendar.data}
                  <Star size="1em" outlined />
                  {#if Math.round(monthStars.current) < 10}
                    <span class="hidden">0</span>
                  {/if}
                  {Math.round(monthStars.current)} / {calendar.data
                    .totalStars}
                  &nbsp;
                  <Star size="1em" class="green" outlined />
                  {#if Math.round(monthGreenStars.current) < 10}
                    <span class="hidden">0</span>
                  {/if}
                  {Math.round(monthGreenStars.current)} / {calendar.data
                    .totalGreenStars}
                {/if}
              </span>
            </div>
            <ActionButton iconOnly onClick={handleNextMonth}>
              <ArrowRight />
            </ActionButton>
          </div>
        </th>
      </tr>
      <tr>
        {#each days as day}
          <th>{day}</th>
        {/each}
      </tr>
    </thead>
    <tbody bind:contentRect={calendarBox}>
      {#each calendar.data?.dates ?? [] as row}
        <tr>
          {#each row as cell}
            {#key cell.date}
              <td>
                <button
                  class:disabled={cell.disabled}
                  class="date-cell"
                  class:hidden={cell.empty}
                  class:selected={cell.date === selectedDate.current}
                  onclick={() => handleClickCell(cell)}
                  onkeypress={() => handleClickCell(cell)}
                >
                  <span class="date-text">
                    {parseDate(cell.date)?.getDate()}
                  </span>
                  <StarContainer
                    stars={cell.stars}
                    fullClear={cell.fullClear}
                  />
                </button>
              </td>
            {/key}
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<style lang="scss">
  @use 'sass:color';
  @use '~/theme/include-media.scss' as *;

  .loading {
    position: absolute;
    background-color: #00000040;
    border-radius: 0 0 8px 8px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .header {
    display: flex;
    flex-direction: column;
  }
  table {
    table-layout: fixed;
    width: 100%;
    border-collapse: collapse;
  }
  th {
    width: 2.5em;
    border-bottom: 2px solid white;
    padding: 0.5em 0;
  }
  .month {
    font-size: 1.5em;
  }
  .total-stars {
    font-family: monospace;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    :global(svg) {
      margin-right: 0.5em;
    }
    .hidden {
      opacity: 0;
    }
  }
  .calendar-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  th,
  td {
    text-align: center;
  }
  td {
    padding: 0.25em;
  }
  .date-cell {
    width: 100%;
    margin: auto;
    transition: all 0.25s ease-in;
    $color: hsl(213, 56%, 71%);
    transition: all 0.25s ease-in;
    cursor: pointer;
    font-weight: bold;
    color: #2f3640;
    background-color: color.scale($color, $lightness: 80%);

    padding: 0.75em 0.5em;
    border: 2px solid #00000000;
    border-radius: 999px;
    cursor: pointer;
    &.hidden {
      opacity: 0;
    }
    &.selected {
      border-color: #2f3640;
    }
    &:hover {
      background-color: color.scale($color, $lightness: 40%);
    }
    &:active {
      background-color: color.scale($color, $lightness: 20%);
    }
    &.disabled {
      color: gray;
      &:hover {
        background-color: color.scale($color, $lightness: 80%);
        cursor: initial;
      }
    }
    :global(html.dark-mode) & {
      $color: #2f3640;
      color: white;
      background-color: $color;
      &:hover {
        background-color: color.scale($color, $lightness: 10%);
      }
      &.selected {
        border-color: white;
      }
      &:active {
        background-color: color.scale($color, $lightness: 20%);
      }
      &.disabled {
        color: gray;
        &:hover {
          background-color: color.scale($color);
          cursor: initial;
        }
      }
    }
  }

  .date-text {
    font-weight: bold;
    font-size: 1.5em;
    line-height: 1;
  }

  @include media('<=tablet') {
    .date-cell {
      padding: 0.5em;
    }
  }

  @include media('<=500px') {
    td {
      padding: 2px;
    }

    .date-cell {
      padding: 0.5em 0;
    }

    .date-text {
      font-size: 1.25em;
    }
  }
</style>
